import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, Save, X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const BulkSubcategoryModal = ({ isOpen, onClose, category, onSuccess }) => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [results, setResults] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize with existing subcategories
  useEffect(() => {
    if (isOpen && category) {
      const existingSubcategories = (category.subcategories || []).map(sub => ({
        id: sub.id,
        name: sub.name,
        slug: sub.slug,
        description: sub.description || '',
        hsn_code: sub.hsn_code || '',
        sac_code: sub.sac_code || '',
        gst_default_rate: sub.gst_config?.default_rate || 18,
        is_active: sub.is_active !== false,
        sort_order: sub.sort_order || 0,
        isNew: false,
        isModified: false
      }));
      
      setSubcategories(existingSubcategories);
      setResults(null);
      setHasChanges(false);
    }
  }, [isOpen, category]);

  const createEmptyRow = () => ({
    id: null,
    name: '',
    slug: '',
    description: '',
    hsn_code: '',
    sac_code: '',
    gst_default_rate: 18,
    is_active: true,
    sort_order: subcategories.length,
    isNew: true,
    isModified: false
  });

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleFieldChange = (index, field, value) => {
    const updated = [...subcategories];
    updated[index][field] = value;
    updated[index].isModified = true;
    
    // Auto-generate slug when name changes (only for new subcategories)
    if (field === 'name' && updated[index].isNew) {
      updated[index].slug = generateSlug(value);
    }
    
    setSubcategories(updated);
    setHasChanges(true);
  };

  const addRow = () => {
    setSubcategories([...subcategories, createEmptyRow()]);
    setHasChanges(true);
  };

  const removeRow = (index) => {
    const sub = subcategories[index];
    
    if (sub.isNew) {
      // Just remove new rows directly
      setSubcategories(subcategories.filter((_, i) => i !== index));
    } else {
      // Mark existing rows as inactive (soft delete)
      const updated = [...subcategories];
      updated[index].is_active = false;
      updated[index].isModified = true;
      setSubcategories(updated);
    }
    setHasChanges(true);
  };

  const handleSubmit = async () => {
    // Separate new and modified subcategories
    const newSubcategories = subcategories.filter(sub => sub.isNew && sub.name.trim() && sub.slug.trim());
    const modifiedSubcategories = subcategories.filter(sub => !sub.isNew && sub.isModified);
    
    if (newSubcategories.length === 0 && modifiedSubcategories.length === 0) {
      toast({
        title: 'No Changes',
        description: 'No changes to save',
        variant: 'default'
      });
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      let addedCount = 0;
      let updatedCount = 0;
      let errors = [];

      // Add new subcategories
      if (newSubcategories.length > 0) {
        const addResponse = await apiCall(`/api/categories/${category.id}/subcategories/bulk-add`, {
          method: 'POST',
          body: JSON.stringify({ subcategories: newSubcategories })
        });
        addedCount = addResponse.summary?.added || 0;
        if (addResponse.errors?.length) {
          errors = [...errors, ...addResponse.errors];
        }
      }

      // Update modified subcategories
      if (modifiedSubcategories.length > 0) {
        const updateResponse = await apiCall(`/api/categories/${category.id}/subcategories/bulk-update`, {
          method: 'PUT',
          body: JSON.stringify({ subcategories: modifiedSubcategories })
        });
        updatedCount = updateResponse.summary?.updated || 0;
        if (updateResponse.errors?.length) {
          errors = [...errors, ...updateResponse.errors];
        }
      }

      setResults({
        summary: { added: addedCount, updated: updatedCount, errors: errors.length },
        errors
      });

      if (addedCount > 0 || updatedCount > 0) {
        toast({
          title: 'Success',
          description: `Added ${addedCount}, Updated ${updatedCount} subcategories`
        });
        setHasChanges(false);
        onSuccess?.();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save subcategories',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Count stats
  const activeCount = subcategories.filter(s => s.is_active && !s.isNew).length;
  const newCount = subcategories.filter(s => s.isNew).length;
  const modifiedCount = subcategories.filter(s => !s.isNew && s.isModified).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">
              Manage Subcategories
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Category: <span className="font-medium">{category?.name}</span>
              <span className="ml-4 text-gray-400">
                {activeCount} existing • {newCount > 0 && <span className="text-green-600">{newCount} new</span>}
                {modifiedCount > 0 && <span className="text-blue-600 ml-2">{modifiedCount} modified</span>}
              </span>
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 mb-2 text-xs font-medium text-gray-500 uppercase tracking-wide px-2 sticky top-0 bg-white pb-2 border-b">
            <div className="col-span-2">Name *</div>
            <div className="col-span-2">Slug *</div>
            <div className="col-span-2">Description</div>
            <div className="col-span-1">HSN Code</div>
            <div className="col-span-1">SAC Code</div>
            <div className="col-span-1">GST %</div>
            <div className="col-span-1">Order</div>
            <div className="col-span-1 text-center">Active</div>
            <div className="col-span-1"></div>
          </div>

          {/* Rows */}
          <div className="space-y-2">
            {subcategories.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No subcategories yet. Click &quot;+ Add Subcategory&quot; below to create one.
              </div>
            ) : (
              subcategories.map((sub, index) => (
                <div 
                  key={sub.id || `new-${index}`} 
                  className={`grid grid-cols-12 gap-2 items-center p-2 rounded-lg border transition-colors ${
                    sub.isNew 
                      ? 'bg-green-50 border-green-200' 
                      : sub.isModified 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-gray-50 border-transparent hover:border-gray-200'
                  }`}
                >
                  <div className="col-span-2">
                    <Input
                      value={sub.name}
                      onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      placeholder="Subcategory name"
                      className="text-sm h-9"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      value={sub.slug}
                      onChange={(e) => handleFieldChange(index, 'slug', e.target.value)}
                      placeholder="slug"
                      className="text-sm h-9"
                      disabled={!sub.isNew}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      value={sub.description}
                      onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                      placeholder="Description"
                      className="text-sm h-9"
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      value={sub.hsn_code}
                      onChange={(e) => handleFieldChange(index, 'hsn_code', e.target.value)}
                      placeholder="84713010"
                      maxLength={8}
                      className="text-sm h-9 font-mono"
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      value={sub.sac_code}
                      onChange={(e) => handleFieldChange(index, 'sac_code', e.target.value)}
                      placeholder="998599"
                      maxLength={6}
                      className="text-sm h-9 font-mono"
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      value={sub.gst_default_rate}
                      onChange={(e) => handleFieldChange(index, 'gst_default_rate', parseFloat(e.target.value) || 0)}
                      min={0}
                      max={28}
                      className="text-sm h-9"
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      value={sub.sort_order}
                      onChange={(e) => handleFieldChange(index, 'sort_order', parseInt(e.target.value) || 0)}
                      className="text-sm h-9"
                    />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <input
                      type="checkbox"
                      checked={sub.is_active}
                      onChange={(e) => handleFieldChange(index, 'is_active', e.target.checked)}
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRow(index)}
                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      title={sub.isNew ? 'Remove' : 'Deactivate'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add Row Button - Always visible at the bottom */}
          <Button
            variant="outline"
            onClick={addRow}
            className="mt-4 w-full border-dashed border-2 text-gray-600 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Subcategory
          </Button>

          {/* Results */}
          {results && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
              <h4 className="font-medium mb-2">Results</h4>
              <div className="flex gap-6 text-sm">
                {results.summary?.added > 0 && (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Added: {results.summary.added}</span>
                  </div>
                )}
                {results.summary?.updated > 0 && (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-500" />
                    <span>Updated: {results.summary.updated}</span>
                  </div>
                )}
                {results.summary?.errors > 0 && (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <span>Errors: {results.summary.errors}</span>
                  </div>
                )}
              </div>
              
              {results.errors?.length > 0 && (
                <div className="mt-2 text-sm text-red-600">
                  {results.errors.map((e, i) => (
                    <p key={e.id || e.name || `${e.title || "e"}-${i}`}>{e.name}: {e.error}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {hasChanges && (
              <span className="text-amber-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                You have unsaved changes
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading || !hasChanges}
              className="min-w-[140px]"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkSubcategoryModal;
